import { getFields, isHowYouHearAboutUs, ItemRestrictionErrors } from "common";
import { concat, countBy, escapeRegExp, isEmpty, isNil, isNumber, maxBy, omit, sortBy, startsWith } from "lodash";
import {
    AddonFieldResponse,
    CatalogItem,
    CheckInVerificationStatus,
    Field,
    FieldResponse,
    FieldType,
    Form,
    FormResponse,
    FormResponseFields,
    Guest,
    LineItem,
    LineItemTemplate,
    ProductCatalogItem,
    Purchase,
    PurchaseItem,
} from "types";
import { GuestPayload } from "../components/PurchaseModification/ChangeQuantity/AddGuest";
import {
    CreatePurchaseItemLineItem,
    CreatePurchaseItemState,
    CreatePurchaseState,
} from "../stores/createPurchaseStore";
import { getDemographicLineItems, getMembershipLineItems } from "./PurchaseItem";
import { getMarketingConsentFields } from "@/models/Form/Form";

export const getAnswerCount = (guest: Guest, form?: Form) => {
    const total = guest.formResponse?.fields.filter((field) => hasFieldValue(field)).length ?? 0;
    if (form) {
        const offset = getMarketingConsentFields(form).filter((field) => field.organizerOnly && guest.primary).length;

        return total - offset;
    }

    return total;
};

export const hasFieldValue = (field: FieldResponse) => {
    if ((field.value?.toString() ?? "").length > 0) {
        return true;
    }

    return field.type === FieldType.CHECKBOX;
};

export const hasEmptyRequiredFields = (
    guest: Guest,
    form: Form,
    initialFormResponse?: FormResponse,
    isOrganizerOnly?: boolean,
) => {
    const requiredFields = getFields(form, guest.primary, isOrganizerOnly).filter((field) => {
        const isNotCheckbox = field.type !== FieldType.CHECKBOX;
        const isPickupExcluded = !(field.type === FieldType.PICKUP && field.organizerOnly);
        const isMarketingConsentExcluded = !(field.type === FieldType.MARKETING_CONSENT && field.organizerOnly);
        const isHowYouHearAboutUsExcluded = !(isHowYouHearAboutUs(field) && field.organizerOnly);

        return (
            field.required &&
            isNotCheckbox &&
            isPickupExcluded &&
            isMarketingConsentExcluded &&
            isHowYouHearAboutUsExcluded
        );
    });

    const formResponse = initialFormResponse ?? guest.formResponse;

    if (!guest.name) {
        /** XS-25 : Guest name would be required for all the demographics */
        return true;
    }

    return requiredFields.some((field) => {
        if (!formResponse) {
            return true;
        }

        return formResponse.fields.some((fieldResponse) => {
            return (
                fieldResponse.type === field.type &&
                fieldResponse.label === field.label &&
                fieldResponse.value.toString().trim().length === 0
            );
        });
    });
};

export const getGuestEmail = (guest: Guest) => {
    return guest.formResponse?.fields.find((field) => field.label === FormResponseFields.EMAIL)?.value ?? "";
};

export const getGuestPhone = (guest: Guest) => {
    return guest.formResponse?.fields.find((field) => field.label === FormResponseFields.PHONE)?.value ?? "";
};

export const getPrimaryGuest = (guests: Guest[]) => {
    return guests.find((guest) => isPrimaryGuest(guest)) ?? guests[0];
};

export const isOrganizerSelected = (guests?: Guest[]) => {
    return guests?.some((guest) => guest.primary);
};

export const getFieldValue = (field: Field, guest: Guest) => {
    return guest.formResponse?.fields.find((guestField) => guestField.label === field.label)?.value ?? "";
};

/** The order guests are listed to the seller: unsaved guests first, then existing ones by sequence. */
export const sortGuestsByDisplayOrder = (guests: GuestPayload[]) => {
    const existingGuests = guests.filter((guest) => !isNil(guest.sequence));
    const newGuests = guests.filter((guest) => isNil(guest.sequence));

    return concat(newGuests, sortBy(existingGuests, "sequence"));
};

export const hasLinkedWaiver = (guest: GuestPayload) => {
    return Boolean(guest.waiver?.id);
};

const hasAnyIdentifyingData = (guest: GuestPayload, demographicCatalogItems: ProductCatalogItem[]) => {
    if (guest.primary) {
        return true;
    }

    if (hasLinkedWaiver(guest)) {
        return true;
    }

    if (!isSystemGeneratedName(guest, demographicCatalogItems)) {
        return true;
    }

    return guest.formResponse?.fields.some(({ value }) => value && value !== "");
};

export const isSystemGeneratedName = (guest: GuestPayload, catalog: CatalogItem | ProductCatalogItem[]) => {
    if (Array.isArray(catalog)) {
        const regexPatterns = catalog.map(
            (catalogItem) => new RegExp(`${escapeRegExp(catalogItem.name)}\\s+#\\d+\\s*$`),
        );
        return regexPatterns.some((regexPattern) => regexPattern.test(guest.name));
    }

    const regexPattern = new RegExp(`${escapeRegExp(catalog.name)}\\s+#\\d+\\s*$`);
    return regexPattern.test(guest.name);
};

export const getIdentifiedGuests = (guests: GuestPayload[], demographicCatalogItems: ProductCatalogItem[]) => {
    return guests.filter((guest: GuestPayload) => hasAnyIdentifyingData(guest, demographicCatalogItems));
};

export const getUnIdentifiedGuests = (guests: GuestPayload[], demographicCatalogItems: ProductCatalogItem[]) => {
    return guests.filter((guest: GuestPayload) => !hasAnyIdentifyingData(guest, demographicCatalogItems));
};

export const getLastGuestCount = (count: number, catalogName: string, guests: Guest[]) => {
    // Generating a new system generated name, considering duplicate demographic system generated names already in the purchase
    const testString = `${catalogName} #`;
    const demographicCount = guests.map((guest) => {
        if (startsWith(guest.name, testString)) {
            const number = Number.parseInt(guest.name.split(testString)[1], 10);
            return isNumber(number) ? number : count;
        }

        return count;
    });

    return maxBy(demographicCount) ?? 0;
};

export const getGuestPlaceholderName = (
    guest: Guest,
    serialNumber: number,
    lineItems: Array<CreatePurchaseItemLineItem | LineItem>,
) => {
    const demographicsMap: Record<ID, string> = {};

    for (const lineItem of getDemographicLineItems({ lineItems })) {
        const { template, name } = lineItem;

        if (template.id && name) {
            demographicsMap[template.id] = name;
        }
    }

    let demographicName = demographicsMap[guest.template!.id] ?? "Guest";

    for (const lineItem of getMembershipLineItems({ lineItems })) {
        if (lineItem.template.id === guest.template!.id) {
            demographicName = "Member";
        }
    }

    return `${demographicName} #${serialNumber}`;
};

/**
 * Takes a collection of `guests` as an argument and checks if any of the guest name is empty.
 * If yes, adds an auto-generated name in **`DemographicsName #{serialNumber}`** format.
 */
export const updateGuestsWithPlaceholderNames = (guests: Guest[], lineItems: CreatePurchaseItemLineItem[]) => {
    let demographicsInitialIndex = 0;

    return guests.map((guest, index) => {
        if (index >= 1 && guest.unit.id !== guests[index - 1].unit.id) {
            demographicsInitialIndex = index;
        }

        if (guest.name) {
            return guest;
        }

        const serialNumber = index - demographicsInitialIndex + 1;
        const guestName = getGuestPlaceholderName(guest, serialNumber, lineItems);

        return { ...guest, name: guestName };
    });
};

export const isPrimaryGuest = (
    guest: Guest,
    purchase?: Purchase | CreatePurchaseState,
    item?: PurchaseItem | CreatePurchaseItemState,
) => {
    if (guest.primary || item?.quantity === 1) {
        return true;
    }

    if (purchase?.organizer && purchase.organizer.id === guest.traveler?.id) {
        if (!item?.guests || item.guests.length === 0) {
            return true;
        }
        const currentGuestIndex = item.guests.findIndex((g) => g.id === guest.id);
        if (currentGuestIndex === -1) {
            return false;
        }
        // Find the first guest in the list who matches the organizer's id.
        const organizerId = purchase.organizer.id;
        const firstOrganizerGuestIndex = item.guests.findIndex((g) => g.traveler?.id === organizerId);

        // The guest is primary only if they are the FIRST instance of the organizer in the guest list.
        // need this check in case there are multiple guests with the same traveler id (e.g. same email for all guests).
        if (currentGuestIndex === firstOrganizerGuestIndex) {
            return true;
        }
    }

    // For a lot of bookings with multiple guests, all guests primary will be false. We assume that the first guest is primary
    if (item && "quantity" in item && item.quantity > 1 && !guest.primary) {
        // If there is no other primary in the list, then the first guest is primary
        const hasOtherPrimary = item.guests?.some((g) => g.primary);
        const guestIndex = item.guests?.findIndex((g) => g.id === guest.id);
        return guestIndex === 0 && !hasOtherPrimary;
    }

    return guest.primary;
};

export const hasCheckInWithVerificationStatus = (guest: Guest, verifiedOnly = false): boolean => {
    return !!guest.checkIns?.some((checkIn) => {
        const hasVerificationStatus = !checkIn.deletedAt && checkIn.verificationStatus;
        if (verifiedOnly) {
            return hasVerificationStatus && checkIn.verificationStatus === CheckInVerificationStatus.ID_VERIFIED;
        }

        return hasVerificationStatus;
    });
};

export const hasIdVerifiedCheckIn = (guest: Guest) => {
    return hasCheckInWithVerificationStatus(guest, true);
};

export const getGuestsWithSameUnit = (item: CreatePurchaseItemState, demographicTemplate?: LineItemTemplate) => {
    return item.guests?.filter((guest) => guest.unit.id === demographicTemplate?.unit?.id);
};

const isAddonResponse = (field: FieldResponse): field is AddonFieldResponse => field.type === FieldType.ADDON;

const isAddonOverridden = (field: FieldResponse) => Boolean((field as AddonFieldResponse)._overrideRestrictions);

export const hasAddonFieldsInGuest = (guest: Guest) => {
    return guest.formResponse?.fields.some((field) => isAddonResponse(field));
};

/** A saved answer has the template id. An unsaved one has only the template code (sku). */
export const isAnswerToAddon = (response: FieldResponse, templateId?: ID, templateCode?: string) => {
    const answer = response as AddonFieldResponse;
    const isSameCode = Boolean(templateCode) && answer.sku === templateCode;
    const isSameTemplate = Boolean(templateId) && answer.lineItemTemplate?.id === templateId;

    return response.type === FieldType.ADDON && (isSameTemplate || isSameCode);
};

export const isAddonOverriddenByGuests = (guests: Guest[], templateId?: ID, templateCode?: string) => {
    return guests.some((guest) => {
        return guest.formResponse?.fields?.some((field) => {
            return isAnswerToAddon(field, templateId, templateCode) && isAddonOverridden(field);
        });
    });
};

const hasAddonIdentity = (response: FieldResponse) => {
    const { sku, lineItemTemplate } = response as AddonFieldResponse;
    return Boolean(sku || lineItemTemplate?.id);
};

/**
 * Copies one attendee's add-on overrides to the other attendees, returning only those whose flag changes. An answer
 * left untouched has no sku or template, so it is matched by label, unless two add-ons share that label.
 */
export const getGuestsWithSharedAddonOverrides = (guests: Guest[], updatedGuest: Guest) => {
    const addonAnswers = (updatedGuest.formResponse?.fields ?? []).filter((field): field is AddonFieldResponse => {
        return isAddonResponse(field);
    });
    const labelCounts = countBy(addonAnswers, "label");
    const isSameAddon = (field: FieldResponse, answer: AddonFieldResponse) => {
        if (hasAddonIdentity(field) && hasAddonIdentity(answer)) {
            return isAnswerToAddon(field, answer.lineItemTemplate?.id, answer.sku);
        }

        return field.type === FieldType.ADDON && field.label === answer.label && labelCounts[answer.label] === 1;
    };

    return guests.flatMap((guest) => {
        if (guest.id === updatedGuest.id || !guest.formResponse) {
            return [];
        }

        let isChanged = false;
        const fields = guest.formResponse.fields.map((field) => {
            const source = addonAnswers.find((answer) => isSameAddon(field, answer));
            const isOverriding = Boolean(source?._overrideRestrictions);
            if (!source || isAddonOverridden(field) === isOverriding) {
                return field;
            }

            isChanged = true;
            return { ...field, _overrideRestrictions: isOverriding };
        });

        return isChanged ? [{ ...guest, formResponse: { ...guest.formResponse, fields } }] : [];
    });
};

/** The API names an attendee by their position in the item's guests; -1 matches no error. */
export const getGuestIndex = (guests: Guest[] | undefined, guest: Guest) => {
    return guests?.findIndex((itemGuest) => itemGuest.id === guest.id) ?? -1;
};

/**
 * An error naming an answer marks the attendee who gave it. A booking limit error marks every attendee who has the
 * add-on, since any of them can lower it.
 */
export const hasAddonLimitError = (
    guest: Guest,
    guestIndex: number,
    restrictionErrors: ItemRestrictionErrors | undefined,
    catalogItems: CatalogItem[],
) => {
    const hasGuestError = !isEmpty(restrictionErrors?.guests[guestIndex]);
    if (hasGuestError) {
        return true;
    }

    return Object.keys(restrictionErrors?.errors ?? {}).some((templateId) => {
        const catalogItem = catalogItems.find((item) => item.template?.id === templateId);
        const templateCode = catalogItem?.template?.code;
        const hasAnswer = guest.formResponse?.fields?.some((field) => {
            return isAnswerToAddon(field, templateId, templateCode) && Number(field.value) > 0;
        });

        return Boolean(hasAnswer);
    });
};

/** The attendee just saved, plus every other attendee whose shared add-on override changed with it. */
export const getGuestsToSave = (updatedGuest: Guest, guests: Guest[] = [], omittedKeys: Array<keyof Guest> = []) => {
    const changedGuests = [updatedGuest, ...getGuestsWithSharedAddonOverrides(guests, updatedGuest)];
    return changedGuests.map((guest) => omit(guest, omittedKeys) as Guest);
};

export const applySharedAddonOverrides = (guests: Guest[], updatedGuest: Guest) => {
    const changedGuests = getGuestsWithSharedAddonOverrides(guests, updatedGuest);

    return guests.map((guest) => changedGuests.find((changed) => changed.id === guest.id) ?? guest);
};

/**
 * "Ignore questionnaire for now" overrides questionnaire add-on limits, and turning it off clears every attendee's
 * override. The API applies it only to limits the seller allows to be overridden.
 */
export const withQuestionnaireAddonOverrides = (guest: Guest, isOverriding = true) => {
    const isChanged = guest.formResponse?.fields.some((field) => {
        return isAddonResponse(field) && isAddonOverridden(field) !== isOverriding;
    });
    if (!guest.formResponse || !isChanged) {
        return guest;
    }

    const fields = guest.formResponse.fields.map((field) => {
        if (!isAddonResponse(field)) {
            return field;
        }

        return isOverriding ? { ...field, _overrideRestrictions: true } : omit(field, "_overrideRestrictions");
    });

    return { ...guest, formResponse: { ...guest.formResponse, fields } };
};
